
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { fetchExamTypes } from '../../../../api/examApi';
import { fetchCategories } from '../../../../api/categoryApi';
import './ViewQuestion.css';
import { FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CommonDataTable from '../../../../components/CustomTable/CustomTable';
import axiosInstance from '../../../../api/axiosInstance';
import { updateQuestion } from '../../../../api/questionApi';

import Spinner from '../../../../components/Spinner/Spinner';


const ViewQuestion = () => {
    const [questions, setQuestions] = useState([]);
    const [examTypes, setExamTypes] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedExamType, setSelectedExamType] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [categorySearch, setCategorySearch] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
    const [isEditCategoryDropdownOpen, setIsEditCategoryDropdownOpen] = useState(false);
    const [editCategorySearch, setEditCategorySearch] = useState('');

    // useEffect(() => {
    //     fetchExamTypesData();
    //     fetchQuestionsData();
    // }, [currentPage, perPage]);

    useEffect(() => {
        if (selectedExamType) {
            fetchCategoriesData(selectedExamType);
        } else {
            setCategories([]);
            setSelectedCategory(null);
            setCategorySearch('');
        }
    }, [selectedExamType]);

    const fetchExamTypesData = async () => {
        try {
            const examTypesData = await fetchExamTypes();
            setExamTypes(examTypesData);
        } catch (error) {
            console.error('Error fetching exam types:', error);
            toast.error('Oops! Finhaat hit a data snag. Try again soon!');
        }
    };

    const fetchCategoriesData = async (examTypeId) => {
        try {
            const categoriesData = await fetchCategories();
            const filteredCategories = categoriesData.filter(cat => cat.examType.examTypeId === parseInt(examTypeId));
            setCategories(filteredCategories);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Oops! Finhaat hit a data snag. Try again soon!');
        }
    };

    const fetchQuestionsData = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.post('/questions/dashboard', {
                search: "isDeleted:eq:0",
                sortBy: "category.categoryId",
                sortDirection: "asc",
                page: 0,  
                size: 100000, 
            });
            setQuestions(response.data.content); 
            setTotalElements(response.data.totalElements); 
        } catch (error) {
            console.error('Error fetching questions:', error);
            toast.error('Oops! Failed to fetch questions. Try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);



    useEffect(() => {
        fetchExamTypesData();
        fetchQuestionsData();
    }, [fetchQuestionsData]);

    const handleExamTypeChange = (e) => {
        setSelectedExamType(e.target.value);
        setSelectedCategory(null);
        setCategorySearch('');
    };

    const handleCategorySearchChange = (e) => {
        setCategorySearch(e.target.value);
        setIsCategoryDropdownOpen(true);
        setSelectedCategory(null);
    };

    const handleCategorySelect = (category) => {
        setSelectedCategory(category);
        setCategorySearch(category.categoryName);
        setIsCategoryDropdownOpen(false);
    };

    const handleEdit = async (question) => {
        setEditingQuestion(question);
        setIsEditModalOpen(true);
        setEditCategorySearch(question.category.categoryName);

        if (question.examType && question.examType.examTypeId) {
            await fetchCategoriesData(question.examType.examTypeId);
        }
    };

    const handleDelete = async (questionId) => {
        try {
            await axiosInstance.post(`/questions/delete?id=${questionId}`, {}, {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            toast.success('Question deleted successfully!');
            fetchQuestionsData();

        } catch (error) {
            console.error('Error deleting question:', error);
            toast.error('Oops! Failed to delete the question. Please try again.');
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            await updateQuestion(editingQuestion);
            toast.success('Question updated successfully!');
            setIsEditModalOpen(false);
            fetchQuestionsData();
        } catch (error) {
            console.error('Error updating question:', error);
            toast.error('Oops! Failed to update the question. Please try again.');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditingQuestion(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleOptionChange = (index, value) => {
        setEditingQuestion(prevState => ({
            ...prevState,
            options: prevState.options.map((option, i) =>
                i === index ? { ...option, optionText: value } : option
            )
        }));
    };

    const handleEditCategorySearchChange = (e) => {
        setEditCategorySearch(e.target.value);
        setIsEditCategoryDropdownOpen(true);
    };

    const handleEditCategorySelect = (category) => {
        setEditingQuestion(prevState => ({
            ...prevState,
            category: category
        }));
        setEditCategorySearch(category.categoryName);
        setIsEditCategoryDropdownOpen(false);
    };


    const filteredQuestions = useMemo(() => {
        // Filter all questions based on the selected exam type and category
        return questions.filter(question =>
            (selectedExamType ? question.examType.examTypeId === parseInt(selectedExamType) : true) &&
            (selectedCategory ? question.category.categoryId === selectedCategory.categoryId : true)
        );
    }, [questions, selectedExamType, selectedCategory]);


    const filteredCategories = useMemo(() => {
        return categories.filter(cat =>
            cat.categoryName.toLowerCase().includes(categorySearch.toLowerCase())
        );
    }, [categories, categorySearch]);

    const filteredEditCategories = useMemo(() => {
        return categories.filter(cat =>
            cat.categoryName.toLowerCase().includes(editCategorySearch.toLowerCase())
        );
    }, [categories, editCategorySearch]);

    const columns = [
        {
            name: 'Question',
            selector: row => row.description,
            sortable: true,
            wrap: true,
        },
        {
            name: 'Option A',
            cell: row => (
                <div className={`option-cell ${row.correctAnswer.includes('A') ? 'correct-option' : ''}`}>
                    {row.options.find(opt => opt.optionSrNo === 'A')?.optionText}
                </div>
            ),
        },
        {
            name: 'Option B',
            cell: row => (
                <div className={`option-cell ${row.correctAnswer.includes('B') ? 'correct-option' : ''}`}>
                    {row.options.find(opt => opt.optionSrNo === 'B')?.optionText}
                </div>
            ),
        },
        {
            name: 'Option C',
            cell: row => (
                <div className={`option-cell ${row.correctAnswer.includes('C') ? 'correct-option' : ''}`}>
                    {row.options.find(opt => opt.optionSrNo === 'C')?.optionText}
                </div>
            ),
        },
        {
            name: 'Option D',
            cell: row => (
                <div className={`option-cell ${row.correctAnswer.includes('D') ? 'correct-option' : ''}`}>
                    {row.options.find(opt => opt.optionSrNo === 'D')?.optionText}
                </div>
            ),
        },
        {
            name: 'Actions',
            cell: row => (
                <div>
                    <button onClick={() => handleEdit(row)} className="action-btn edit-btn">
                        <FaEdit />
                    </button>
                    <button onClick={() => handleDelete(row.questionId)} className="action-btn delete-btn">
                        <FaTrash />
                    </button>
                </div>
            ),
        },
    ];

    const ExpandedComponent = ({ data }) => (
        <div className="expanded-row">
             <h4 className='expanded-row-explanation-h4'>Category:</h4>
             <p className='expanded-row-explanation'>{data.category ? data.category.categoryName : 'No category assigned'}</p>

            <h4 className='expanded-row-explanation-h4'>Explanation:</h4>
            <p className='expanded-row-explanation'>{data.explanation}</p>
        </div>
    );

    const handlePageChange = (page) => {
        setCurrentPage(page - 1);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        setPerPage(newPerPage);
        setCurrentPage(page - 1);
    };

    useEffect(() => {
        setTotalElements(filteredQuestions.length);
    }, [filteredQuestions]);

    return (
        <div className="view-question-container">
            <div className="total-elements-info hidden">
                <p>Total Questions: {totalElements}</p>
            </div>
         {/* Show spinner while data is loading */}
         {isLoading ? (
            <Spinner />
        ) : (
            <>

            <div className="view-question-container-filters">
                <label className="view-question-container-filter-label">
                    Select Exam Type:
                    <select
                        value={selectedExamType}
                        onChange={handleExamTypeChange}
                        className="view-question-container-filter-select"
                    >
                        <option value="">All</option>
                        {examTypes.map(type => (
                            <option key={type.examTypeId} value={type.examTypeId}>
                                {type.examTypeDesc}
                            </option>
                        ))}
                    </select>
                </label>

                <label className="view-question-container-filter-label">
                    Select Category:
                    <div className="category-search-container">
                        <input
                            type="text"
                            value={categorySearch}
                            onChange={handleCategorySearchChange}
                            onFocus={() => setIsCategoryDropdownOpen(true)}
                            onBlur={() => setTimeout(() => setIsCategoryDropdownOpen(false), 200)}
                            placeholder="Search categories..."
                            className="category-search-input"
                        />
                        <FaSearch className="category-search-icon" />
                        {isCategoryDropdownOpen && (
                            <ul className="category-dropdown">
                                {filteredCategories.map(cat => (
                                    <li
                                        key={cat.categoryId}
                                        onMouseDown={() => handleCategorySelect(cat)}
                                        className="category-dropdown-item"
                                    >
                                        {cat.categoryName}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </label>
            </div>

            <CommonDataTable
                columns={columns}
                data={filteredQuestions.slice(currentPage * perPage, (currentPage + 1) * perPage)}
                totalRows={filteredQuestions.length}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                pagination
                paginationPerPage={perPage}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                paginationServer
                progressPending={isLoading}
                expandableRows
                expandableRowsComponent={ExpandedComponent}
            />
              </>          
        )}

            {isEditModalOpen && editingQuestion && (
                <div className="modal-overlay-question-updation">
                    <div className="modal-container-question-updation">
                        <form onSubmit={handleUpdate} className="view-question-form">
                            <h3>Edit Question</h3>

                            <div className="form-group">
                                <label>Exam Type:</label>
                                <select
                                    name="examTypeId"
                                    value={editingQuestion.examType.examTypeId}
                                    onChange={handleInputChange}
                                >
                                    {examTypes.map(type => (
                                        <option key={type.examTypeId} value={type.examTypeId}>
                                            {type.examTypeDesc}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Category:</label>
                                <div className="category-search-container">
                                    <input
                                        type="text"
                                        value={editCategorySearch}
                                        onChange={handleEditCategorySearchChange}
                                        onFocus={() => setIsEditCategoryDropdownOpen(true)}
                                        onBlur={() => setTimeout(() => setIsEditCategoryDropdownOpen(false), 200)}
                                        placeholder="Search categories..."
                                        className="category-search-input"
                                    />
                                    <FaSearch className="category-search-icon" />
                                    {isEditCategoryDropdownOpen && (
                                        <ul className="category-dropdown">
                                            {filteredEditCategories.map(cat => (
                                                <li
                                                    key={cat.categoryId}
                                                    onMouseDown={() => handleEditCategorySelect(cat)}
                                                    className="category-dropdown-item"
                                                >
                                                    {cat.categoryName}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Question Description:</label>
                                <textarea
                                    name="description"
                                    value={editingQuestion.description}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="checkbox-group">
                                <label>Multiple Options:</label>
                                <input
                                    type="checkbox"
                                    name="isMultiSelection"
                                    checked={editingQuestion.isMultiSelection === 1}
                                    onChange={() => setEditingQuestion(prevState => ({
                                        ...prevState,
                                        isMultiSelection: prevState.isMultiSelection === 1 ? 0 : 1
                                    }))}
                                />
                            </div>

                            <div className="form-group">
                                <label>Options:</label>
                                {['A', 'B', 'C', 'D'].map((option, index) => (
                                    <div key={index} className="option-group">
                                        <input
                                            type="text"
                                            value={editingQuestion.options[index]?.optionText || ''}
                                            onChange={(e) => handleOptionChange(index, e.target.value)}
                                            placeholder={`Option ${option}`}
                                            required
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="form-group">
                                <label>Correct Option:</label>
                                <input
                                    type="text"
                                    name="correctAnswer"
                                    value={editingQuestion.correctAnswer}
                                    onChange={handleInputChange}
                                    placeholder={editingQuestion.isMultiSelection === 1 ? 'Correct Options: A, B' : 'Correct Option: A'}
                                    required
                                />
                            </div>

                            <div className="form-group">
                                <label>Explanation:</label>
                                <textarea
                                    name="explanation"
                                    value={editingQuestion.explanation}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <button type="submit" className="submit-btn">
                                Update
                            </button>
                            <button type="button" onClick={() => setIsEditModalOpen(false)} className="cancel-btn">
                                Cancel
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ViewQuestion;